import axios from 'axios';
import { API_BASE_URL } from '../constants/api';

// API_BASE_URL already includes /api, so we don't add it again
const VOLUNTEER_REGISTRATION_URL = `${API_BASE_URL}/volunteer-registration`;

// Helper to get auth headers for protected routes (cookie-based; kept for backward compat)
const getAuthHeaders = () => {
  return {};
};

const VolunteerRegistrationService = {
  /**
   * Submit a new volunteer registration with waiver signature
   * @param {Object} registrationData - The registration data
   * @param {string} registrationData.fullName - Volunteer's full name
   * @param {string} registrationData.phoneNumber - Volunteer's phone number
   * @param {string} registrationData.email - Volunteer's email
   * @param {string} registrationData.eventId - Selected event ID
   * @param {string} registrationData.eventName - Selected event name
   * @param {string} registrationData.eventDate - Selected event date
   * @param {string} registrationData.eventLocation - Selected event location
   * @param {string} registrationData.medicalExperience - Medical experience level
   * @param {string} registrationData.volunteerArea - Desired volunteer area
   * @param {boolean} registrationData.acknowledgedRisks - Waiver acknowledgment 1
   * @param {boolean} registrationData.acknowledgedLiability - Waiver acknowledgment 2
   * @param {boolean} registrationData.acknowledgedSafetyInstructions - Waiver acknowledgment 3
   * @param {boolean} registrationData.acknowledgedEmergencyAuth - Waiver acknowledgment 4
   * @param {boolean} registrationData.acknowledgedMedicalExpenses - Waiver acknowledgment 5
   * @param {string} registrationData.signatureData - Signature data (base64 for drawn, text for typed)
   * @param {string} registrationData.signatureType - 'draw' or 'type'
   * @param {string} registrationData.typedSignature - Typed signature text if signatureType is 'type'
   * @param {string} registrationData.printedName - Printed name
   * @param {string} registrationData.waiverVersion - Version of the waiver
   * @returns {Promise<Object>} Response with registrationId and downloadToken
   */
  submitRegistration: async (registrationData) => {
    try {
      const response = await axios.post(
        `${VOLUNTEER_REGISTRATION_URL}/public`,
        registrationData
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error submitting volunteer registration:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to submit registration',
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Get the PDF download URL for a registration
   * @param {string} registrationId - The registration ID
   * @param {string} downloadToken - The download token
   * @returns {string} The PDF download URL
   */
  getPdfDownloadUrl: (registrationId, downloadToken) => {
    return `${VOLUNTEER_REGISTRATION_URL}/public/${registrationId}/pdf?token=${downloadToken}`;
  },

  /**
   * Download the PDF for a registration
   * @param {string} registrationId - The registration ID
   * @param {string} downloadToken - The download token
   * @returns {Promise<Object>} Response with blob or error
   */
  downloadPdf: async (registrationId, downloadToken) => {
    try {
      const response = await axios.get(
        `${VOLUNTEER_REGISTRATION_URL}/public/${registrationId}/pdf`,
        {
          params: { token: downloadToken },
          responseType: 'blob'
        }
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error downloading PDF:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to download PDF',
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Admin functions

  /**
   * Get all volunteer registrations (admin)
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page
   * @param {string} options.status - Filter by status
   * @returns {Promise<Object>} Response with registrations list
   */
  getAllRegistrations: async (options = {}) => {
    try {
      const response = await axios.get(VOLUNTEER_REGISTRATION_URL, { 
        params: options,
        headers: getAuthHeaders()
      });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching registrations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch registrations',
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Get registrations for a specific event (admin)
   * @param {string} eventId - The event ID
   * @returns {Promise<Object>} Response with registrations list
   */
  getRegistrationsByEvent: async (eventId) => {
    try {
      const response = await axios.get(`${VOLUNTEER_REGISTRATION_URL}/event/${eventId}`, {
        headers: getAuthHeaders()
      });
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching registrations by event:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch registrations',
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Get a single registration by ID (admin)
   * @param {string} registrationId - The registration ID
   * @returns {Promise<Object>} Response with registration data
   */
  getRegistrationById: async (registrationId) => {
    try {
      const response = await axios.get(`${VOLUNTEER_REGISTRATION_URL}/${registrationId}`, {
        headers: getAuthHeaders()
      });
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching registration:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch registration',
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Get volunteer registrations by email (admin)
   * @param {string} email - The volunteer's email
   * @returns {Promise<Object>} Response with registrations list
   */
  getRegistrationsByEmail: async (email) => {
    try {
      const response = await axios.get(`${VOLUNTEER_REGISTRATION_URL}/by-email/${encodeURIComponent(email)}`, {
        headers: getAuthHeaders()
      });
      return {
        success: true,
        data: response.data.data,
        count: response.data.count
      };
    } catch (error) {
      console.error('Error fetching registration by email:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch registration',
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Update volunteer registration status (admin)
   * @param {string} registrationId - The registration ID
   * @param {string} status - New status ('approved', 'rejected', 'pending', 'checked-in', 'completed', 'cancelled')
   * @param {string} notes - Optional admin notes
   * @returns {Promise<Object>} Response with updated registration
   */
  updateStatus: async (registrationId, status, notes = '') => {
    try {
      const response = await axios.patch(
        `${VOLUNTEER_REGISTRATION_URL}/${registrationId}/status`,
        { status, notes },
        { headers: getAuthHeaders() }
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating registration status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update status',
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Sync all approved registrations to User accounts (admin)
   * Creates User records for approved volunteers that don't have one yet.
   * @returns {Promise<Object>} Response with sync summary
   */
  syncApprovedToUsers: async () => {
    try {
      const response = await axios.post(
        `${VOLUNTEER_REGISTRATION_URL}/sync-approved`,
        {},
        { headers: getAuthHeaders() }
      );
      return {
        success: true,
        data: response.data.summary,
        details: response.data.details,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error syncing approved volunteers:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to sync volunteers',
        error: error.response?.data?.error || error.message
      };
    }
  }
};

export default VolunteerRegistrationService;
